/**
 * ==========================================================================
 *  THE TUOPPI REPLACING KIMMO'S BOTTLE
 * ==========================================================================
 *  The glass the porukka in the juhlasali sent him for: empty from pöytä 2 to
 *  the baari, full from the baari to his own chair.
 *
 *  It is the same object the level draws thirty of - a stack rather than a
 *  container, because nothing in this blockout is transparent and a glass
 *  therefore cannot have beer *inside* it. What you read is the level in it,
 *  never the glass, which is exactly what has to be readable here: half the
 *  errand is taking the glass and the other half is filling it, and the only
 *  thing that tells the two apart at a glance is whether the thing in the
 *  corner of the screen is pale or amber.
 *
 *  The drink is four meshes, with a handle beside the glass:
 *
 *      empty  ->  a ring of dregs in the bottom, and glass all the way up
 *      part   ->  beer as far as it has got, a head on it, glass above that
 *      full   ->  beer to the rim, and a head standing proud of it
 *
 *  The middle row is the whole reason this is drawn at all rather than
 *  switched. `SIMA.pourDuration` is two seconds of standing at a counter, and
 *  two seconds of nothing happening is indistinguishable from a key that did
 *  not register; the beer climbing the glass in the corner of the screen is
 *  what says the wait is going somewhere. It is the only progress bar in the
 *  game and it is not shaped like one.
 *
 *  Same three tricks as the bottle and the billet: parented to the camera,
 *  drawn with depth testing off and last, and left out of the fog. Cosmetic
 *  from end to end - `Sima` in src/world/ owns both facts this reads, and
 *  nothing here is ever read back.
 * ==========================================================================
 */

import * as THREE from 'three';
import { TUOPPI_VIEWMODEL } from '../config/viewmodel';
import { reachCurve, tipCurve } from './BeerSwing';

export interface TuoppiFrame {
  /** Render seconds since the last frame. */
  dt: number;
  /** Whether there is a glass in Kimmo's hand at all. */
  holding: boolean;
  /** And how much of a pint is in it, 0 .. 1. `Sima.fill`, straight through. */
  fill: number;
  /** Horizontal ground speed, m/s. Drives the bob. */
  speed: number;
  /** The camera's head bob this frame, in metres. */
  bobLateral: number;
  bobVertical: number;
  swayIntensity: number;
}

/** One part of the stack, and the three materials its cylinder is drawn with. */
interface Part {
  mesh: THREE.Mesh;
  materials: THREE.MeshBasicMaterial[];
}

/**
 * A one-pixel-tall strip of `color` shaded the way a cylinder standing under a
 * light is, wrapped once around the glass.
 *
 * The viewmodels in this game are drawn with `MeshBasicMaterial` - no lighting,
 * because they hang off the camera and a lit object there swings through the
 * hall's lamps every time you turn round. A box survives that: `KlapiViewmodel`
 * gives its six faces six colours by hand and the billet reads as a solid. A
 * cylinder does not, because every one of its sides is the same face - flat
 * fill it and you get a rectangle with rounded ends, which is a paper bag and
 * not a pint.
 *
 * So the shading is painted instead of lit, exactly the way every surface in
 * the level is (`src/render/surfaces.ts`): brightest a little to the left of
 * centre, where the hall's lamps are relative to a glass carried in the right
 * hand, and falling off round both sides. Thirty-two pixels is plenty - the
 * texture is stretched over about a hundred on screen and a smooth ramp is what
 * it is meant to be.
 */
function wrapShading(color: number, ctx2d: CanvasRenderingContext2D): THREE.Texture {
  const c = new THREE.Color(color);
  const steps = ctx2d.canvas.width;
  for (let i = 0; i < steps; i++) {
    const around = (i / steps) * Math.PI * 2;
    // 1.0 at the highlight, about 0.6 at the back. Never zero: an unlit side
    // that goes black reads as a hole in the glass rather than as its far side.
    const lit = 0.62 + 0.42 * Math.max(0, Math.cos(around - Math.PI * 0.3));
    ctx2d.fillStyle = `rgb(${Math.round(c.r * 255 * lit)},${Math.round(c.g * 255 * lit)},${Math.round(c.b * 255 * lit)})`;
    ctx2d.fillRect(i, 0, 1, 1);
  }
  const texture = new THREE.CanvasTexture(ctx2d.canvas);
  texture.wrapS = THREE.RepeatWrapping;
  return texture;
}

/**
 * Puts one part of the stack `height` tall with its foot at `y`.
 *
 * The geometry is a unit cylinder scaled per part, and every measurement in
 * here is from the bottom of the glass while `CylinderGeometry` is centred on
 * its own middle - so this is the one place that conversion is done, and the
 * pour moves three of the four parts every frame it runs.
 */
function stand(part: Part, y: number, height: number): void {
  part.mesh.scale.y = Math.max(height, 1e-5);
  part.mesh.position.y = y + height / 2;
}

export class TuoppiViewmodel {
  private readonly pivot = new THREE.Group();
  private readonly geometry: THREE.CylinderGeometry;
  private readonly handleGeometry = new THREE.TorusGeometry(0.033, 0.009, 6, 12);
  private readonly parts: Part[] = [];
  /** The four, by the state each belongs to. */
  private readonly dregs: Part;
  private readonly wall: Part;
  private readonly beer: Part;
  private readonly foam: Part;
  /** 0 while empty-handed, 1 while carrying. Everything else is derived. */
  private shown = 0;
  private bob = 0;
  private sipTime = 1;

  constructor(private readonly camera: THREE.PerspectiveCamera) {
    // One unit cylinder, scaled per part. Ten sides, the same count the level's
    // own glasses are drawn with - a tuoppi held at arm's length is still a
    // tuoppi, and a smoother one here would read as a different object.
    this.geometry = new THREE.CylinderGeometry(1, 1, 1, 10, 1);

    const V = TUOPPI_VIEWMODEL;
    /**
     * Base at `y`, `height` tall, `radius` across.
     *
     * Three materials, in the order `CylinderGeometry` groups its faces: the
     * wall, the top and the bottom. `capLit` is what the top is worth relative
     * to the wall, and it is the one number here that is doing real work - a
     * glass looked down into is *dark* at the top and a head of foam is the
     * brightest thing on the whole object, so it is the caps that say which of
     * the two you are holding.
     */
    const part = (
      color: number,
      radius: number,
      height: number,
      y: number,
      capLit: number,
    ): Part => {
      const flat = (tint: number): THREE.MeshBasicMaterial =>
        new THREE.MeshBasicMaterial({
          color: new THREE.Color(color).multiplyScalar(tint),
          transparent: true,
          depthTest: false,
          depthWrite: false,
          fog: false,
        });
      const wall = new THREE.MeshBasicMaterial({
        map: wrapShading(color, this.paint()),
        transparent: true,
        depthTest: false,
        depthWrite: false,
        fog: false,
      });
      const materials = [wall, flat(capLit), flat(0.55)];

      const mesh = new THREE.Mesh(this.geometry, materials);
      mesh.renderOrder = 999;
      mesh.frustumCulled = false;
      mesh.scale.set(radius, height, radius);
      // CylinderGeometry is centred on its own middle, and every measurement
      // above is from the bottom of the glass - which is the way `tuoppi()`
      // stacks one and the only way the two states line up.
      mesh.position.y = y + height / 2;
      this.pivot.add(mesh);
      const item = { mesh, materials };
      this.parts.push(item);
      return item;
    };

    // Empty: a ring of what the last mouthful left, and the glass over it. The
    // glass's own top is the darkest cap on the object, because that is what
    // looking down into an empty pint actually looks like.
    this.dregs = part(V.spill, V.radius * 0.92, V.dregs, 0, 0.9);
    this.wall = part(V.glass, V.radius, V.height - V.dregs, V.dregs, 0.42);
    // Full: beer to the rim, and the collar on top of it. Drawn a shade wider
    // than the beer for the same reason the level's is - a head is what has
    // climbed the *glass*, so it stands on the rim rather than in the liquid.
    // And its top is the one cap that is brighter than its own wall.
    this.beer = part(V.beer, V.radius * 0.92, V.height, 0, 0.9);
    this.foam = part(V.foamColor, V.radius, V.foam, V.height, 1.15);

    const handleMaterial = new THREE.MeshBasicMaterial({
      color: V.glass, transparent: true, depthTest: false, depthWrite: false, fog: false,
    });
    const handle = new THREE.Mesh(this.handleGeometry, handleMaterial);
    handle.position.set(V.radius + 0.014, V.height * 0.5, 0);
    handle.scale.set(0.85, 1.35, 1);
    handle.renderOrder = 998;
    handle.frustumCulled = false;
    this.pivot.add(handle);
    this.parts.push({ mesh: handle, materials: [handleMaterial] });

    this.pivot.visible = false;
    camera.add(this.pivot);
  }

  reset(): void {
    this.shown = 0;
    this.bob = 0;
    this.sipTime = 1;
    this.pivot.visible = false;
  }

  sip(): void {
    this.sipTime = 0;
  }

  update(frame: TuoppiFrame): void {
    // Picked up and put down over a couple of frames rather than switched, so
    // the glass arrives in the hand instead of appearing in it.
    this.sipTime = Math.min(1, this.sipTime + frame.dt);
    const target = frame.holding ? 1 : 0;
    const rate = frame.dt / TUOPPI_VIEWMODEL.raiseDuration;
    this.shown += Math.max(-rate, Math.min(rate, target - this.shown));

    this.pivot.visible = this.shown > 0.002;
    if (!this.pivot.visible) return;

    // Which parts are up, and how tall each of them is. Read straight off the
    // pour with no smoothing of its own: `fill` already moves at the rate the
    // simulation pours at, and a viewmodel that eased it would be showing a
    // glass that is fuller or emptier than the one the porukka is judging.
    //
    // The layout is `tuoppi()`'s in src/config/level.ts, part for part, which
    // is what keeps the glass in the hand the same object as the glass on the
    // table: beer from the bottom, the head standing on top of it, and what is
    // left of the glass reaching up to the rim.
    const V = TUOPPI_VIEWMODEL;
    const beer = V.height * frame.fill;
    const wallBottom = frame.fill > 0 ? Math.min(V.height, beer + V.foam) : V.dregs;

    this.dregs.mesh.visible = frame.fill <= 0;
    this.beer.mesh.visible = frame.fill > 0;
    this.foam.mesh.visible = frame.fill > 0;
    // Gone once the head has climbed to the rim, which is what a full one is.
    this.wall.mesh.visible = V.height - wallBottom > 1e-4;
    stand(this.beer, 0, beer);
    stand(this.foam, beer, V.foam);
    stand(this.wall, wallBottom, V.height - wallBottom);

    const halfHeight =
      Math.tan(THREE.MathUtils.degToRad(this.camera.fov) / 2) * TUOPPI_VIEWMODEL.depth;
    const halfWidth = halfHeight * this.camera.aspect;

    this.bob +=
      frame.dt *
      Math.min(frame.speed, TUOPPI_VIEWMODEL.bobSpeedCap) *
      TUOPPI_VIEWMODEL.bobRate;
    const sway = frame.swayIntensity;
    const reach = reachCurve(this.sipTime);
    const tip = tipCurve(this.sipTime);
    const x =
      TUOPPI_VIEWMODEL.x +
      (0.18 - TUOPPI_VIEWMODEL.x) * reach +
      Math.sin(this.bob) * TUOPPI_VIEWMODEL.bobAmount +
      frame.bobLateral * sway;
    const y =
      TUOPPI_VIEWMODEL.y -
      0.08 * reach -
      (1 - this.shown) * TUOPPI_VIEWMODEL.raiseFrom +
      Math.sin(this.bob * 2) * TUOPPI_VIEWMODEL.bobAmount * 0.6 +
      frame.bobVertical * sway;

    this.pivot.position.set(x * halfWidth, y * halfHeight, -TUOPPI_VIEWMODEL.depth);
    // The roll gets the bob and the other two do not: a glass being carried is
    // kept level by the wrist and rocked by the shoulder, which is one axis of
    // give and not three.
    this.pivot.rotation.set(
      TUOPPI_VIEWMODEL.pitch + 1.15 * tip,
      TUOPPI_VIEWMODEL.yaw,
      TUOPPI_VIEWMODEL.roll + 0.25 * tip + Math.sin(this.bob) * 0.05,
    );
    for (const item of this.parts) {
      for (const material of item.materials) material.opacity = this.shown;
    }
  }

  /** A fresh 32x1 canvas for one part's wrapped shading. See `wrapShading`. */
  private paint(): CanvasRenderingContext2D {
    const canvas = document.createElement('canvas');
    canvas.width = 32;
    canvas.height = 1;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('no 2d context for the tuoppi viewmodel');
    return ctx;
  }

  dispose(): void {
    this.camera.remove(this.pivot);
    this.geometry.dispose();
    this.handleGeometry.dispose();
    for (const item of this.parts) {
      for (const material of item.materials) {
        material.map?.dispose();
        material.dispose();
      }
    }
  }
}
